import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, Download, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { hasCap, isPureEmployee } from '@/lib/capabilities';
import { downloadHoursPdf } from '@/lib/hoursPdfDownload';
import { apiFetch } from '@/lib/supabaseClient';

const pad = (value) => String(value).padStart(2, '0');
const dateIso = (date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};
const safeName = (value) => String(value || 'Dipendente')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'Dipendente';

function periodLabel(period, anchor) {
  const date = new Date(`${anchor}T12:00:00`);
  if (period === 'month') return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
  if (period === 'week') {
    const monday = new Date(date);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return `${dateIso(monday)}_${dateIso(sunday)}`;
  }
  return '';
}

function employeeLabel(person) {
  return person?.display_name || person?.full_name
    || [person?.nome, person?.cognome].filter(Boolean).join(' ') || 'Dipendente';
}

function accessAllowed(me, mode) {
  if (mode === 'self') {
    return Boolean((me?.role_codes || []).includes('DIPENDENTE') && isPureEmployee(me) && hasCap(me, 'attendance.self'));
  }
  const roles = me?.role_codes || [];
  if (roles.includes('TITOLARE')) {
    return hasCap(me, 'attendance.export');
  }
  if (roles.includes('CEO')) {
    return hasCap(me, 'attendance.export');
  }
  return roles.some(role => role !== 'DIPENDENTE') && hasCap(me, 'attendance.export');
}

export default function HoursPdfExport({
  mode = 'self',
  people = [],
  companies = [],
  locations = [],
  departments = [],
  filters = {},
  isSimulated = false,
  testId = 'hours-pdf-export',
  selectedMonth,
  onSelectedMonthChange,
}) {
  const { me } = useAuth() || {};
  const allowed = accessAllowed(me, mode);
  const today = useMemo(() => dateIso(new Date()), []);
  const [period, setPeriod] = useState('month');
  const validSelectedMonth = /^\d{4}-\d{2}$/.test(String(selectedMonth || ''));
  const [anchorDate, setAnchorDate] = useState(`${validSelectedMonth ? selectedMonth : today.slice(0, 7)}-01`);
  const [dateFrom, setDateFrom] = useState(`${today.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [employeeId, setEmployeeId] = useState(filters.employee_id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [tenantActive, setTenantActive] = useState(mode !== 'admin' || !(me?.role_codes || []).includes('CEO'));

  useEffect(() => {
    if (mode === 'admin') setEmployeeId(filters.employee_id || '');
  }, [filters.employee_id, mode]);

  useEffect(() => {
    if (period === 'month' && validSelectedMonth && anchorDate.slice(0, 7) !== selectedMonth) {
      setAnchorDate(`${selectedMonth}-01`);
    }
  }, [anchorDate, period, selectedMonth, validSelectedMonth]);

  useEffect(() => {
    if (mode !== 'admin' || !(me?.role_codes || []).includes('CEO') || !allowed) return undefined;
    let mounted = true;
    apiFetch('/api/ceo/tenant-context')
      .then((context) => { if (mounted) setTenantActive(Boolean(context?.active_tenant_id)); })
      .catch(() => { if (mounted) setTenantActive(false); });
    return () => { mounted = false; };
  }, [allowed, me?.role_codes, mode]);

  const selectedPerson = people.find((person) => String(person.id) === String(employeeId));
  const rangeDays = dateFrom && dateTo
    ? (Date.parse(`${dateTo}T00:00:00Z`) - Date.parse(`${dateFrom}T00:00:00Z`)) / 86_400_000 + 1
    : 0;
  const rangeInvalid = period === 'custom' && (
    !dateFrom || !dateTo || dateFrom > dateTo || !Number.isFinite(rangeDays) || rangeDays > 366
  );

  const exportPdf = async () => {
    if (!allowed || busy || rangeInvalid) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom') {
        params.set('date_from', dateFrom);
        params.set('date_to', dateTo);
      } else {
        params.set('anchor_date', anchorDate);
      }
      if (mode === 'admin') {
        if (employeeId) params.set('employee_id', employeeId);
        if (filters.company_id) params.set('company_id', filters.company_id);
        if (filters.location_id) params.set('location_id', filters.location_id);
        if (filters.department_id) params.set('department_id', filters.department_id);
      }

      const periodPart = period === 'custom'
        ? `${dateFrom}_${dateTo}`
        : periodLabel(period, anchorDate);
      const filename = `RestaurantOS_Ore_${safeName(mode === 'self' ? (me?.full_name || me?.name) : employeeId ? employeeLabel(selectedPerson) : 'Dipendenti')}_${periodPart}.pdf`;
      const result = await downloadHoursPdf(`/api/attendance/hours/pdf?${params.toString()}`, filename);
      if (result?.cancelled) setMessage('Condivisione annullata.');
      else setMessage('PDF pronto per il download.');
    } catch (downloadError) {
      if (downloadError?.status === 401) setError('Sessione scaduta. Accedi nuovamente e riprova.');
      else if (downloadError?.name === 'TypeError' || downloadError?.message === 'Failed to fetch') {
        setError('Connessione assente. Verifica la rete e riprova.');
      } else setError(downloadError?.message || 'Non è stato possibile generare il PDF.');
    } finally {
      setBusy(false);
    }
  };

  if (!allowed || !tenantActive || isSimulated) return null;
  const enabledCompanies = companies;
  const enabledLocations = locations.filter((item) => !filters.company_id || String(item.company_id) === String(filters.company_id));
  const enabledDepartments = departments.filter((item) => (
    (!filters.location_id || String(item.location_id) === String(filters.location_id))
    && (!filters.company_id || enabledLocations.some((location) => String(location.id) === String(item.location_id)))
  ));

  const compactSelf = mode === 'self';
  return (
    <section className={`rounded-xl border border-app-border bg-app-surface shadow-sm ${compactSelf ? 'p-2.5' : 'p-3 md:p-4'}`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <span className={`flex shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-brand-green ${compactSelf ? 'h-8 w-8' : 'h-9 w-9'}`}>
          <CalendarDays className={compactSelf ? 'h-4 w-4' : 'h-4 w-4'} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={compactSelf ? 'text-xs font-semibold text-brand-navy' : 'text-sm font-semibold text-brand-navy'}>{mode === 'self' ? 'Scarica le mie ore' : 'Esporta ore in PDF'}</h2>
          <p className="mt-0.5 text-xs text-stone-500">{compactSelf ? 'Scegli il periodo del PDF.' : 'Riepilogo ufficiale · fuso orario Europe/Rome'}</p>
        </div>
      </div>

      <div className={`mt-2 grid gap-2 ${compactSelf ? 'grid-cols-2' : 'sm:grid-cols-2'}`}>
        <label className={`space-y-1 font-medium text-stone-600 ${compactSelf ? 'text-[11px]' : 'text-xs'}`}>
          Periodo
          <select value={period} onChange={(event) => setPeriod(event.target.value)} className={`w-full rounded-lg border border-app-border-strong bg-app-surface px-2 text-sm text-brand-navy ${compactSelf ? 'h-9' : 'h-10'}`} data-testid={`${testId}-period`}>
            <option value="week">Settimana</option>
            <option value="month">Mese</option>
            <option value="custom">Personalizzato</option>
          </select>
        </label>
        {period !== 'custom' ? (
          <label className={`space-y-1 font-medium text-stone-600 ${compactSelf ? 'text-[11px]' : 'text-xs'}`}>
            {period === 'week' ? 'Una data della settimana' : 'Mese selezionato'}
            <input
              type={period === 'month' ? 'month' : 'date'}
              value={period === 'month' && validSelectedMonth ? selectedMonth : period === 'month' ? anchorDate.slice(0, 7) : anchorDate}
              onChange={(event) => {
                const value = event.target.value;
                setAnchorDate(period === 'month' ? `${value}-01` : value);
                if (period === 'month' && /^\d{4}-\d{2}$/.test(value)) onSelectedMonthChange?.(value);
              }}
              className={`w-full rounded-lg border border-app-border-strong bg-app-surface px-2 text-sm text-brand-navy ${compactSelf ? 'h-9' : 'h-10'}`}
              data-testid={`${testId}-anchor`}
            />
          </label>
        ) : (
          <div className={`grid grid-cols-2 gap-2 ${compactSelf ? 'col-span-2' : ''}`}>
            <label className={`space-y-1 font-medium text-stone-600 ${compactSelf ? 'text-[11px]' : 'text-xs'}`}>Dal
              <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className={`w-full rounded-lg border border-app-border-strong bg-app-surface px-2 text-sm text-brand-navy ${compactSelf ? 'h-9' : 'h-10'}`} data-testid={`${testId}-date-from`} />
            </label>
            <label className={`space-y-1 font-medium text-stone-600 ${compactSelf ? 'text-[11px]' : 'text-xs'}`}>Al
              <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className={`w-full rounded-lg border border-app-border-strong bg-app-surface px-2 text-sm text-brand-navy ${compactSelf ? 'h-9' : 'h-10'}`} data-testid={`${testId}-date-to`} />
            </label>
          </div>
        )}
      </div>

      {mode === 'admin' && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="space-y-1 text-xs font-medium text-stone-600">Dipendente
            <select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)} className="h-10 w-full rounded-lg border border-app-border-strong bg-app-surface px-2 text-sm text-brand-navy" data-testid={`${testId}-employee`}>
              <option value="">Tutti i dipendenti autorizzati</option>
              {people.map((person) => <option key={person.id} value={person.id}>{employeeLabel(person)}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-3 gap-1.5 text-[10px] text-stone-500" aria-label="Perimetro applicato">
            <span className="truncate" title={companies.find((item) => String(item.id) === String(filters.company_id))?.name || 'Tutte le aziende'}>
              {companies.find((item) => String(item.id) === String(filters.company_id))?.name || 'Tutte le aziende'}
            </span>
            <span className="truncate" title={enabledLocations.find((item) => String(item.id) === String(filters.location_id))?.name || 'Tutti i locali'}>
              {enabledLocations.find((item) => String(item.id) === String(filters.location_id))?.name || 'Tutti i locali'}
            </span>
            <span className="truncate" title={enabledDepartments.find((item) => String(item.id) === String(filters.department_id))?.name || 'Tutti i reparti'}>
              {enabledDepartments.find((item) => String(item.id) === String(filters.department_id))?.name || 'Tutti i reparti'}
            </span>
          </div>
          <span className="sr-only">{enabledCompanies.length} aziende nel catalogo autorizzato</span>
        </div>
      )}

      {rangeInvalid && <p className="mt-2 text-xs text-amber-800" role="alert">Intervallo non valido: seleziona date consecutive entro 366 giorni.</p>}
      {error && <p className="mt-2 flex items-start gap-1.5 text-xs text-red-800" role="alert"><AlertCircle className="h-4 w-4 shrink-0" />{error}</p>}
      {message && <p className="mt-2 text-xs text-brand-green" role="status">{message}</p>}
      <button type="button" onClick={exportPdf} disabled={busy || rangeInvalid} className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-navy px-3 text-sm font-semibold text-white transition hover:bg-brand-navy/90 disabled:cursor-not-allowed disabled:opacity-55" data-testid={`${testId}-download`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
        {busy ? 'Generazione in corso…' : 'Scarica PDF ore'}
      </button>
    </section>
  );
}
